import { TagControlSourcePropertyTypeEnum } from '@/types/schema';

import { ControlTagValues, SelectedTagValues, TagsMenuItem, TagValues } from '../../../types';
import { ControlledByMap, ControllingTagsMap, SourceToTargetMap, TagsIdToNameMap } from '../types';

/**
 * controlledByMap : {
 *      state: country (A state is controlled by a country)
 * }
 *
 * Ex:
 * controlledByMap = {
 *  '6464bae36c4507e700cbe771': ['6464b9e0763eb7f8724bdc9e'],
 *  '6464b9e0763eb7f8724bdc9e': ['6464b8e8763eb7f8724bdb31', '6266a4edf4933e00122a031d'],
 * };
 *
 * The ids are of tags (tag.id)
 */
const getControlledByMap = (tags: TagsMenuItem[]) =>
  tags.reduce((acc, tag) => {
    const controlConfig = tag.settings?.controlConfig;
    if (!controlConfig) return acc;

    const { controls } = controlConfig;
    controls.forEach((control) => {
      if (control.controllingEntity.type === TagControlSourcePropertyTypeEnum.Tag) {
        const controlledTagId = tag.id;

        if (!acc[controlledTagId]?.length) {
          acc[controlledTagId] = [control.controllingEntity.value];
        } else {
          acc[controlledTagId].push(control.controllingEntity.value);
        }
      }
    });

    return acc;
  }, {} as ControlledByMap);

const getTagIdToNameMap = (tags: TagsMenuItem[]): TagsIdToNameMap => {
  return tags.reduce((acc, tag) => {
    acc[tag.id] = tag.name;
    return acc;
  }, {} as TagsIdToNameMap);
};
/**
 * sourceToTargetMap : {
 *    source (option id): {
 *      controlledTagId: target[] (option ids)
 *    }
 * }
 *
 * sourceToTargetMap = {
 *  India(optionId) : {
 *    state(tagId): [Jharkhand(optionId), Bihar(optionId)]
 *  }
 * }
 *
 * Ex:
 * sourceToTargetMap = {
 *   '6464b9008604c946b04874a6': {
 *    '6464bae36c4507e700cbe771': ['6464b9ef8604c946b04874b1', '6464b9fb8604c946b04874b2'],
 *  },
 *  '6464b9058604c946b04874aa': {
 *    '6464bae36c4507e700cbe771': ['6464bab48604c946b04874b9'],
 *  },
 *  '6464b8b88604c946b04874a1': {
 *    '6464b9e0763eb7f8724bdc9e': [
 *      '6464b9008604c946b04874a6',
 *      '6464b9018604c946b04874a7',
 *      '6464b9028604c946b04874a8',
 *    ],
 *  },
 */
const getSourceToTargerMap = (tags: TagsMenuItem[]) =>
  tags.reduce((acc, tag) => {
    const controlConfig = tag.settings?.controlConfig;
    if (!controlConfig) return acc;

    const { controls } = controlConfig;
    controls.forEach((control) => {
      const { rules } = control;
      rules.forEach((rule) => {
        const sourceValues = rule.source.values;
        const targetValues = rule.target.values;
        const controlledTagId = tag.id;

        sourceValues.forEach((sourceValue) => {
          if (!acc[sourceValue]) {
            acc[sourceValue] = {};
          }

          if (!acc[sourceValue][controlledTagId]) {
            acc[sourceValue][controlledTagId] = targetValues;
          } else {
            acc[sourceValue][controlledTagId] = [
              ...acc[sourceValue][controlledTagId],
              ...targetValues,
            ];
          }
        });
      });
    });

    return acc;
  }, {} as SourceToTargetMap);

/**
 * Kind of reverse of controlledByMap
 *
 * A country is controlling states
 * A state is controllinng cities
 *
 * Ex:
 * controllingTagsMap : {
 *  country tag id : [state tag id]
 *  state tag id : [city tag id]
 * }
 *
 * controllingTagsMap = {
 *   '6266a4edf4933e00122a031d': ['6464b9e0763eb7f8724bdc9e'],
 *   '6464b9e0763eb7f8724bdc9e': ['6464bae36c4507e700cbe771'],
 * };
 *
 * The ids are of tags (tag.id)
 */
const getControllingTagsMap = (controlledByMap: ControlledByMap): ControllingTagsMap => {
  const map: Record<string, string[]> = {};

  for (const [controlledTagId, controllingTagIds] of Object.entries(controlledByMap)) {
    controllingTagIds.forEach((controllingTagId) => {
      if (!map[controllingTagId]) {
        map[controllingTagId] = [controlledTagId];
      } else {
        map[controllingTagId].push(controlledTagId);
      }
    });
  }

  return map;
};

/**
 * This function return the final selectedTagValues after any onChange action
 * The return type is SelectedTagValues
 *
 * Ex:
 * India, Us are selected in the country tag
 * Jharkhand, Bihar, California are selected in the state tag
 * Ranchi, Patna, San Francisco are selected in the city tag
 *
 * Note:
 * Country, State, City are tag names
 * India, US, Jharkhand, Bihar, California, Ranchi, Patna, San Francisco are option names
 *
 * Action: US is removed from the country tag
 *
 * This function does the following:
 * 1. Find the updated optionIds of the tag which is being changed -> updatedOptionIds = (Country now has [India])
 * 2. Find the controlled tag ids of the tag which is being changed -> controlledTagIds = (State)
 *        (Note: only state is considered here.
 *          When this function will run recursive, state -> city will be considered)
 * 3. For each controlled tag id, find the selected values of the controlled tag -> selectedValuesOfControlledTag = (Jharkhand, Bihar, California)
 * 4. For each updated option id (India), find the target option ids -> targetOptionIds = (Jharkhand, Bihar, Punjab etc)
 * 5. Filter the already selected options -> filteredOptions = (Jharkhand, Bihar)
 * 6. filteredSelectedValue[state]: { Jharkhand, Bihar }
 * 7. Recursively repeat the process for the tag controlled by State (City)
 */
const getFilteredSelectedValues = ({
  controllingTagsMap,
  sourceToTargetMap,
  tagId,
  val,
  selectedTagValues,
}: {
  tagId: string;
  val: TagValues;
  controllingTagsMap: ControlledByMap;
  sourceToTargetMap: SourceToTargetMap;
  selectedTagValues: SelectedTagValues | undefined;
}) => {
  const updatedValue = val as ControlTagValues;
  const updatedOptionIds = updatedValue?.map((item) => item.value);
  const controlledTagIds = controllingTagsMap[tagId];
  let filteredSelectedValue: SelectedTagValues = {};

  controlledTagIds?.forEach((controlledTagId) => {
    const selectedValuesOfControlledTag = selectedTagValues?.[controlledTagId] as ControlTagValues;

    const allSelectedOptionIds: string[] = [];
    updatedOptionIds?.forEach((optionId) => {
      const tagIdToTargetMap = sourceToTargetMap[optionId];
      const targetOptionIds = tagIdToTargetMap?.[controlledTagId] || [];
      allSelectedOptionIds.push(...targetOptionIds);
    });
    const filteredOptions = selectedValuesOfControlledTag?.filter((item) =>
      allSelectedOptionIds.includes(item.value),
    );

    filteredSelectedValue[controlledTagId] = filteredOptions;
    const connectedControlledTagsSelectedValues = getFilteredSelectedValues({
      tagId: controlledTagId,
      val: filteredOptions,
      controllingTagsMap,
      sourceToTargetMap,
      selectedTagValues,
    });

    filteredSelectedValue = {
      ...filteredSelectedValue,
      ...connectedControlledTagsSelectedValues,
    };
  });

  return filteredSelectedValue;
};

export {
  getControlledByMap,
  getControllingTagsMap,
  getFilteredSelectedValues,
  getSourceToTargerMap,
  getTagIdToNameMap,
};
